/**
 * Messages service - client-side implementation of Messages class
 */

import UniDataTable from '@/common/classes/UniDataTable'
import { callSnackbar } from '@/common/plugins/snackbar'
import { useNotification } from '@/common/composables/useNotification'
import { t } from '@/common/utils/translations'
import type { MessagesService } from './types'
import {
  showInputTextDialog,
  showInputDialog,
  showConfirmDialog as showConfirmDialogUtil,
  showCheckBoxChooserDialog,
  showFilesChooserDialog,
  showFilesToSignChooserDialog,
  showChooserDialog,
  showButtonChooserDialog,
  showButtonChooserByColumnsDialog,
  showChooserWithTableDialog
} from './dialog-manager'

class MessagesServiceImpl implements MessagesService {
  async showMessage(message: string, title: string): Promise<void> {
    try {
      await showConfirmDialogUtil({
        title: title,
        message: message,
        width: '50vw',
        confirmText: t('dialogs.ok'),
        iconClass: 'pi pi-info-circle',
        buttons: [
          { type: 'confirm', label: t('dialogs.ok') }
        ]
      })
    } catch {
      useNotification().info({
        summary: title || t('dialogs.message'),
        detail: message,
      })
    }
  }

  async showMessageError(message: string, title: string): Promise<void> {
    try {
      await showConfirmDialogUtil({
        title: title,
        message: message,
        width: '50vw',
        confirmText: t('dialogs.ok'),
        iconClass: 'pi pi-info-circle',
        iconColor: 'red',
        buttons: [
          { type: 'confirm', label: t('dialogs.ok') }
        ]
      })
    } catch {
      useNotification().error({
        summary: title || t('dialogs.error'),
        detail: message,
      })
    }
  }

  showToast(message: string | Record<string, any>): void {
    const notification = useNotification()
    const rawMessage =
      typeof message === 'string'
        ? message
        : String(
            message?.text ??
              message?.message ??
              message?.detail ??
              message?.summary ??
              '',
          )

    if (!rawMessage) {
      return
    }

    notification.info({
      summary: t('dialogs.notification'),
      detail: rawMessage,
    })

    // TODO: Не знаю, надо это или нет
    callSnackbar({
      text: rawMessage,
      type: 'info',
    })
  }

  async showConfirmDialog(message: string, title: string): Promise<number> {
    const confirmed = await showConfirmDialogUtil({
      title: title || t('dialogs.confirm'),
      message: message,
      confirmText: t('dialogs.ok'),
      cancelText: t('dialogs.cancel')
    })
    return confirmed ? 0 : 1 // 0 = OK, 1 = Cancel
  }

  async checkConfirmation(message: string, title: string): Promise<boolean> {
    return await showConfirmDialogUtil({
      title: title || t('dialogs.confirm'),
      message: message,
      confirmText: t('dialogs.ok'),
      cancelText: t('dialogs.cancel')
    })
  }

  async showCheckBoxChooser(message: string, title: string, variants: UniDataTable): Promise<UniDataTable | null> {
    return await showCheckBoxChooserDialog(message, title, variants)
  }

  async showFilesToSignChooser(
    title: string,
    files: any[],
    selectedIndices: number[],
    maxSize: number,
  ): Promise<UniDataTable | null> {
    return await showFilesToSignChooserDialog(title, files, selectedIndices, maxSize)
  }

  async showFilesChooser(
    title: string,
    files: any[],
    selectedIndices: number[],
    maxSize: number,
    overLimitMessage: string,
    canBeEmpty: boolean,
  ): Promise<UniDataTable | null> {
    return await showFilesChooserDialog(title, files, selectedIndices, maxSize, overLimitMessage, canBeEmpty)
  }

  async showChooser(message: string, title: string, variants: any[][]): Promise<string | null>
  async showChooser(message: string, title: string, variants: UniDataTable, keyColumn: string, descriptionColumn: string): Promise<string | null>
  async showChooser(message: string, title: string, variants: Array<{ name: string; value: any }>): Promise<string | null>
  async showChooser(
    message: string,
    title: string,
    variants: any[][] | UniDataTable | Array<{ name: string; value: any }>,
    keyColumn?: string,
    descriptionColumn?: string,
  ): Promise<string | null> {
    return await showChooserDialog(message, title, variants, keyColumn, descriptionColumn)
  }

  async showInput(message: string, defaultValue: any = ''): Promise<string | null> {
    return await showInputDialog(t('dialogs.input'), message, String(defaultValue || ''))
  }

  async showInputWithTitle(title: string, message: string, defaultValue: any = ''): Promise<string | null> {
    return await showInputDialog(title, message, String(defaultValue || ''))
  }

  async showInputText(title: string, message: string): Promise<string>
  async showInputText(message: string, title: string, def?: string): Promise<string>
  async showInputText(titleOrMessage: string, messageOrTitle: string, def?: string): Promise<string> {
    // Handle both signatures: showInputText(title, message) and showInputText(message, title, def)
    let title: string
    let message: string
    let defaultValue: string = ''

    if (def !== undefined) {
      // showInputText(message, title, def)
      message = titleOrMessage
      title = messageOrTitle
      defaultValue = def || ''
    } else {
      // showInputText(title, message)
      title = titleOrMessage
      message = messageOrTitle
    }

    // Cancel → "" (never null) so onClick `.trim()` is safe.
    return (await showInputTextDialog(title, message, defaultValue)) ?? ''
  }

  async showButtonChooser(message: string, title: string, variants: UniDataTable, keyColumn: string, descriptionColumn: string): Promise<string | null>
  async showButtonChooser(message: string, title: string, variants: any[][]): Promise<string | null>
  async showButtonChooser(message: string, title: string, variants: UniDataTable | any[][], keyColumn?: string, descriptionColumn?: string): Promise<string | null> {
    return await showButtonChooserDialog(message, title, variants, keyColumn, descriptionColumn)
  }

  async showButtonChooserByColumns(message: string, title: string, variants: any[][], buttonsOnRow: number): Promise<string | null> {
    return await showButtonChooserByColumnsDialog(message, title, variants, buttonsOnRow)
  }

  async showChooserWithTable(message: string, title: string, variants: UniDataTable, descriptionColumn: string[]): Promise<string | null> {
    return await showChooserWithTableDialog(message, title, variants, descriptionColumn)
  }
}

export const messagesService = new MessagesServiceImpl()

